import { cn } from 'utils/cn'
import { sourceGroupLabel } from '../utils/loadFields'
import { BoxTruckSourceIcon, isBoxTruckDatSource } from './BoxTruckSourceIcon'

/** A source group present on the board — its chip key, its live load count. */
export interface BoxTruckSourceGroup {
    key: string
    count: number
}

interface BoxTruckSourceBarProps {
    /** Source groups present in the loaded feed (ordered), each with its live count. */
    groups: BoxTruckSourceGroup[]
    /** Deselected group keys — a load whose group is here is hidden. Empty = all shown. */
    hidden: Set<string>
    onToggle: (key: string) => void
}

/**
 * Box Truck Sources bar — the Load Hub source-pill UX (icon + label + live load-count badge) minus its
 * connect/auth logic: every source is always enabled (no "Connect", no status dots). The board always
 * searches SYLECTUS_EMAIL + live DAT; each load carries its source (SYLECTUS, DAT, …) in `sourceType`, so
 * these chips are a client-side filter + count over the returned rows. All selected by default; clicking a
 * pill hides that source's loads. Each count is the rows that source has on the board right now, after the
 * client refinements (see `useBoxTruckBoardView`) — rows LOADED so far, not a server total, so it grows as
 * the operator scrolls further pages in.
 */
export const BoxTruckSourceBar = ({ groups, hidden, onToggle }: BoxTruckSourceBarProps) => {
    if (groups.length === 0) return null

    return (
        <div className="flex flex-wrap items-center gap-2">
            {groups.map(({ key, count }) => {
                const selected = !hidden.has(key)
                return (
                    <button
                        key={key}
                        type="button"
                        onClick={() => onToggle(key)}
                        aria-pressed={selected}
                        className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                            selected ? 'border-accent bg-default text-foreground' : 'border-border bg-surface text-foreground-muted'
                        )}
                    >
                        <span
                            className={cn(
                                'flex shrink-0 items-center justify-center',
                                isBoxTruckDatSource(key) ? 'size-5' : 'size-3.5',
                                !selected && 'opacity-60'
                            )}
                        >
                            <BoxTruckSourceIcon sourceKey={key} />
                        </span>
                        {sourceGroupLabel(key)}
                        <span
                            className={cn(
                                'ml-0.5 min-w-4 rounded-full px-1.5 text-center text-[10px] font-semibold leading-4',
                                selected ? 'bg-accent text-background' : 'bg-default text-foreground-muted'
                            )}
                        >
                            {count}
                        </span>
                    </button>
                )
            })}
        </div>
    )
}
